import { useCallback, useRef, useState } from 'react';
import {
  TASK_STATUSES,
  TASK_STATUS_DOT_CLASSES,
  TASK_STATUS_LABELS,
  type TaskStatus,
} from '../../lib/taskStatus';
import { useDismiss } from '../../lib/useDismiss';
import { FOCUS_RING_FLUSH } from '../ui';
import { CheckIcon } from '../icons';

/**
 * The list row's status cell: what state the task is in, and a menu to move it
 * to any other one.
 *
 * A menu rather than the click-to-cycle the priority cell uses. Priority is a
 * ladder of four and stepping it is almost always what you want; status is not
 * -- BLOCKED sits off the path entirely (see `nextTaskStatus`), and the two
 * most common moves from a row are "this is done" and "this is stuck", which a
 * cycle can only reach by clicking through the states in between.
 *
 * It does not replace the row's ✓ and → buttons. Those are one click each for
 * the two moves that dominate; this is the way to say anything else without
 * opening the task.
 *
 * Below `sm:` it is the dot alone. The label needs about 5rem and the phone
 * template has no such room -- but the column has to survive there anyway,
 * because with grouping turned off the heading that would otherwise carry the
 * status is not on screen at all.
 */
export function StatusCell({
  status,
  taskTitle,
  disabled,
  align = 'right',
  onPick,
}: {
  status: TaskStatus;
  /** Named in the accessible label, so a screen reader hears which row's status
   *  is about to change rather than five identical "Change status" buttons. */
  taskTitle: string;
  disabled: boolean;
  /**
   * Which edge the menu hangs from. Right by default -- see the comment on the
   * panel below -- and left where the cell leads the row, as it does on the
   * dashboard: a right-anchored menu in the first column grows out past the
   * left edge of the card and off under the sidebar, which cuts every label in
   * half.
   */
  align?: 'left' | 'right';
  onPick: (status: TaskStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  useDismiss(containerRef, open, useCallback(() => setOpen(false), []));

  return (
    <span
      // The dismiss ref has to be here, on the element that wraps *both* the
      // trigger and the menu. Without it `containerRef.current` stays null, an
      // outside-click test of `contains` answers false for every target
      // including the menu's own buttons, and the menu unmounts on the
      // mousedown -- so the click that would have picked a status never lands
      // on anything and the row silently does not change.
      ref={containerRef}
      // `z-10` keeps the trigger out from under the row-filling `<Link>`, and
      // the lift while open clears the cells of the rows below -- at an equal
      // z-index the later one in the document would draw through the panel.
      // Same arrangement, and the same reason, as DueDateCell.
      className={`relative ${open ? 'z-20' : 'z-10'}`}
    >
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Status ${TASK_STATUS_LABELS[status]}. Change the status of ${taskTitle}`}
        title={`Status: ${TASK_STATUS_LABELS[status]} — click to change`}
        onClick={() => setOpen((value) => !value)}
        // Two-stage reveal and a gutter-eating hit area, exactly as the priority
        // cell: at rest it is the plain reading of the status, the row's hover
        // draws a hairline to say it is a control, hovering the cell fills it.
        // The `after:` box grows the target vertically to something a thumb can
        // hit without making the row taller.
        className={`relative -mx-1 flex w-[calc(100%+0.5rem)] items-center gap-1 rounded px-1 py-0.5 text-left text-meta text-stone-600 transition after:absolute after:-inset-y-1.5 after:inset-x-0 after:content-[''] hover:bg-stone-200/70 group-hover:ring-1 group-hover:ring-inset group-hover:ring-stone-300 disabled:cursor-default disabled:hover:bg-transparent disabled:group-hover:ring-0 ${FOCUS_RING_FLUSH}`}
      >
        <span
          className={`h-2 w-2 shrink-0 rounded-full ${TASK_STATUS_DOT_CLASSES[status]}`}
          aria-hidden="true"
        />
        {/* Hidden, not dropped: `sr-only` would take it out of flow and the dot
            would stop being centred in its track. The accessible name is on the
            button either way. */}
        <span className="hidden truncate sm:inline">{TASK_STATUS_LABELS[status]}</span>
      </button>

      {open ? (
        // Anchored `right-0` by default because the page's scroll container
        // computes `overflow-x: auto`: a panel growing rightwards out of the
        // last few columns would add a horizontal scrollbar to the whole page.
        // A leading column has the opposite problem and passes `align="left"`.
        // Both spelled out in full, because Tailwind scans source text and
        // would never generate a class name assembled at runtime.
        <div
          role="menu"
          aria-label={`Status of ${taskTitle}`}
          className={`absolute top-full z-20 mt-1.5 w-44 rounded-xl border border-stone-200 bg-white p-1 shadow-lg ${
            align === 'left' ? 'left-0' : 'right-0'
          }`}
        >
          {TASK_STATUSES.map((option) => {
            const current = option === status;

            return (
              <button
                key={option}
                type="button"
                // `menuitemradio`, not `menuitem`: these are five readings of
                // one property and exactly one of them is true right now, which
                // `aria-checked` is what says.
                role="menuitemradio"
                aria-checked={current}
                onClick={() => {
                  setOpen(false);
                  // Re-picking the state the task is already in closes the menu
                  // and stops there: the page would otherwise send a PATCH, and
                  // the reply re-sorts the list and can scroll the row out from
                  // under the pointer -- movement, for a click that meant "no
                  // change after all".
                  if (!current) onPick(option);
                }}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition hover:bg-stone-50 ${
                  current ? 'font-medium text-stone-900' : 'text-stone-700'
                }`}
              >
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${TASK_STATUS_DOT_CLASSES[option]}`}
                  aria-hidden="true"
                />
                <span className="truncate">{TASK_STATUS_LABELS[option]}</span>
                {current ? <CheckIcon className="ml-auto h-3.5 w-3.5 text-stone-400" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </span>
  );
}
